import { Link } from 'react-router-dom';
import {
  ArrowRight, BookOpen, CircuitBoard, Crown, Dices, Feather, FlaskConical, Hourglass, Layers, Library,
  LucideIcon, Moon, Mountain, PawPrint, ScrollText, Shield, Skull, Swords, Tent, TreePine, UserRoundPlus,
} from 'lucide-react';
import { useStrings } from '../locales';
import { useVoidNew } from '../hooks/useVoidNew';
import { useContentVersion } from '../hooks/ContentVersionContext';
import { WhatsNew } from '../components/WhatsNew';
import { NAV_GROUPS } from '../components/Sidebar';
import { pickVersion } from '../character/content/sources';
import { classes } from '../character/content/classes';
import { ancestries } from '../character/content/ancestries';
import { communities } from '../character/content/communities';
import allDomainCards from '../character/content/domains/cards';
import { adversaries } from '../content/adversaries';
import { environments } from '../content/environments';
import './Home.css';

const ICONS: Record<string, LucideIcon> = {
  '/introduction': BookOpen,
  '/character-creation': UserRoundPlus,
  '/classes': Swords,
  '/ancestries': Feather,
  '/communities': Tent,
  '/transformations': Moon,
  '/domains': Layers,
  '/domains?view=cards': Library,
  '/mechanics': Dices,
  '/equipment': Shield,
  '/items': FlaskConical,
  '/beastforms': PawPrint,
  '/gm-tools': ScrollText,
  '/adversaries': Skull,
  '/environments': Mountain,
  '/campaign-frame': TreePine,
  '/kohd': CircuitBoard,
  '/gm-tools?tab=villain%20builder': Crown,
  '/gm-tools?tab=doom%20track': Hourglass,
};

type NavItem = (typeof NAV_GROUPS)[number]['items'][number];

/** A group's links with each sub link (Card Library) under the link the sidebar lists it after (Domains). */
const nestSubs = (items: NavItem[]) =>
  items.reduce<Array<NavItem & { subs: NavItem[] }>>((out, item) => {
    if (item.sub && out.length) out[out.length - 1].subs.push(item);
    else out.push({ ...item, subs: [] });
    return out;
  }, []);

const LIBRARY = NAV_GROUPS.map((g) => ({ ...g, items: nestSubs(g.items.filter((i) => i.path !== '/')) }));
const TECH_PATHS = new Set(NAV_GROUPS.flatMap((g) => g.items).filter((i) => i.tech).map((i) => i.path));
const BETA_PATHS = new Set(NAV_GROUPS.flatMap((g) => g.items).filter((i) => i.beta).map((i) => i.path));

export default function Home() {
  const s = useStrings();
  const h = s.home;
  const { isNew } = useVoidNew();

  const homeLink = (item: NavItem) => {
    const Icon = ICONS[item.path];
    const look = item.tech ? ' home-link--tech' : item.sub ? ' home-link--sub' : '';
    return (
      <Link to={item.path} className={`home-link${look}`}>
        <Icon className="home-link__icon" size={18} aria-hidden="true" />
        <span className="home-link__text">
          <span className="home-link__title">
            {s.nav.items[item.key]}
            {isNew(item.path) && <span className="new-star">*</span>}
            {item.beta && <span className="beta-chip">{s.nav.betaBadge}</span>}
          </span>
          <span className="home-link__desc">{h.descs[item.key as Exclude<typeof item.key, 'home'>]}</span>
        </span>
      </Link>
    );
  };
  const [version] = useContentVersion();

  const stats = [
    { path: '/classes', label: h.stats.classes, count: pickVersion(classes, version).length },
    { path: '/ancestries', label: h.stats.ancestries, count: pickVersion(ancestries, version).length },
    { path: '/communities', label: h.stats.communities, count: pickVersion(communities, version).length },
    { path: '/domains?view=cards', label: h.stats.cards, count: pickVersion(allDomainCards, version, (c) => c.domain).length },
    { path: '/adversaries', label: h.stats.adversaries, count: adversaries.length },
    { path: '/environments', label: h.stats.environments, count: environments.length },
  ];

  return (
    <div className="page-content home">
      <section className="home-hero">
        <div className="home-hero__text">
          <p className="home-hero__kicker"><span className="home-hero__pulse" aria-hidden="true" />{h.heroKicker}</p>
          <h1 className="home-hero__title">{h.heroTitle}</h1>
          <p className="home-hero__subtitle">{h.heroSubtitle}</p>
          <div className="home-hero__ctas">
            <Link to="/character-creation" className="home-cta home-cta--primary">
              <UserRoundPlus size={16} aria-hidden="true" />{h.ctaBuild}
            </Link>
            <Link to="/introduction" className="home-cta">
              <BookOpen size={16} aria-hidden="true" />{h.ctaLearn}
            </Link>
          </div>
        </div>
        <HeroEmblem />
      </section>

      <ul className="home-stats" aria-label={h.statsAria}>
        {stats.map((st) => (
          <li key={st.path}>
            <Link to={st.path} className="home-stat">
              <strong>{st.count}</strong>{' '}<span>{st.label}</span>
            </Link>
          </li>
        ))}
      </ul>

      <WhatsNew />

      <section className="home-section">
        <h2 className="home-heading">{h.toolsHeading}</h2>
        <div className="home-tools">
          {h.tools.map((tool) => {
            const Icon = ICONS[tool.path];
            return (
              <Link key={tool.path} to={tool.path} className={`home-tool${TECH_PATHS.has(tool.path) ? ' home-tool--tech' : ''}`}>
                <span className="home-tool__icon"><Icon size={22} aria-hidden="true" /></span>
                <h3>{tool.title}{BETA_PATHS.has(tool.path) && <span className="beta-chip">{s.nav.betaBadge}</span>}</h3>
                <p>{tool.desc}</p>
                <ArrowRight className="home-tool__go" size={16} aria-hidden="true" />
              </Link>
            );
          })}
        </div>
      </section>

      <section className="home-section">
        <h2 className="home-heading">{h.libraryHeading}</h2>
        <div className="home-library">
          {LIBRARY.map((group) => (
            <div key={group.key} className="home-group">
              <h3 className="home-group__title">{s.nav.groups[group.key]}</h3>
              <ul>
                {group.items.map((item) => (
                  <li key={item.path}>
                    {homeLink(item)}
                    {item.subs.length > 0 && (
                      <ul className="home-subs" aria-label={`Part of ${s.nav.items[item.key]}`}>
                        {item.subs.map((sub) => <li key={sub.path}>{homeLink(sub)}</li>)}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="home-about">
        <div className="intro-blurb">
          <p>
            A website built on <strong>Daggerheart SRD</strong>, {h.introBlurb}
          </p>
        </div>
        <div className="maker-note">
          <p className="maker-note__greeting">{h.makerGreeting}</p>
          <p className="maker-note__text">
            {h.makerText.split('\n').map((line, i, arr) => (
              <span key={i}>{line}{i < arr.length - 1 && <br />}</span>
            ))}
          </p>
        </div>
      </section>
    </div>
  );
}

/** Points on a circle of radius r, starting at the top. */
const ring = (r: number, n: number, offset = 0) =>
  Array.from({ length: n }, (_, i) => {
    const a = ((offset - 90 + (i * 360) / n) * Math.PI) / 180;
    return [r * Math.cos(a), r * Math.sin(a)].map((v) => v.toFixed(2)).join(' ');
  });

// A d12 seen face-on: inner pentagon, decagon outline (0.618 apart), spokes joining them.
const DECAGON = ring(46, 10);
const PENTAGON = ring(46 * 0.618, 5);
const D12_PATH = [
  `M${DECAGON.join('L')}Z`,
  `M${PENTAGON.join('L')}Z`,
  ...PENTAGON.map((p, i) => `M${p}L${DECAGON[i * 2]}`),
].join('');
const TICK_ENDS = ring(54, 12);
const TICKS = ring(60, 12).map((p, i) => `M${p}L${TICK_ENDS[i]}`).join('');

/** Astrolabe: a d12 core with Hope and Fear gems in orbit and circuit traces running off. */
function HeroEmblem() {
  return (
    <svg className="home-emblem" viewBox="-100 -100 200 200" aria-hidden="true">
      <path className="home-emblem__trace" d="M61 -61L70 -70H98M-61 61L-70 70H-98M0 86V100" />
      <circle className="home-emblem__trace-end" cx="98" cy="-70" r="2" />
      <circle className="home-emblem__trace-end" cx="-98" cy="70" r="2" />
      <circle className="home-emblem__dial" r="94" />
      <circle className="home-emblem__rim" r="86" />
      <g className="home-emblem__ticks"><path d={TICKS} /></g>
      <g className="home-emblem__orbit">
        <circle className="home-emblem__path" r="72" />
        <path className="home-emblem__gem home-emblem__gem--hope" d="M72 -6L77 0L72 6L67 0Z" />
        <path className="home-emblem__gem home-emblem__gem--fear" d="M-72 -6L-67 0L-72 6L-77 0Z" />
      </g>
      <path className="home-emblem__die" d={D12_PATH} />
      <path className="home-emblem__spark" d="M0 -9Q1.2 -1.2 9 0Q1.2 1.2 0 9Q-1.2 1.2 -9 0Q-1.2 -1.2 0 -9Z" />
    </svg>
  );
}
